<template>
    <el-dialog :title="localPoint.WayPointID ? $t('pointsManagement.editPoint') : $t('pointsManagement.addPoint')"
        :visible.sync="visible" width="800px" :close-on-click-modal="false" @closed="onDialogClosed">
        <el-form :model="localPoint" :rules="rules" ref="pointForm" size="small" label-width="100px">
            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item :label="$t('pointsManagement.pointName')" prop="WayPointName">
                        <el-input v-model="localPoint.WayPointName"
                            :placeholder="$t('pointsManagement.enterPointName')" maxlength="50"
                            show-word-limit></el-input>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item :label="$t('pointsManagement.enabled')" prop="IsEnabled">
                        <el-switch v-model="IsEnabled" style="margin-top: 8px;"></el-switch>
                    </el-form-item>
                </el-col>
            </el-row>

            <el-row :gutter="20">
                <el-col :span="12">
                    <el-form-item :label="$t('pointsManagement.range')" prop="RangeRadius">
                        <el-input-number v-model="localPoint.RangeRadius" :min="10" :max="5000" :step="10"
                            style="width: 100%;"></el-input-number>
                        <span class="unit-hint">米 (10 ~ 5000)</span>
                    </el-form-item>
                </el-col>
                <el-col :span="12">
                    <el-form-item :label="$t('pointsManagement.maxLeaveTime')" prop="MaxLeaveTime">
                        <el-input-number v-model="localPoint.MaxLeaveTime" :min="1" :max="1440" :step="5"
                            style="width: 100%;"></el-input-number>
                        <span class="unit-hint">分钟 (1 ~ 1440)</span>
                    </el-form-item>
                </el-col>
            </el-row>
            <el-form-item :label="$t('pointsManagement.description')" prop="Remark">
                <el-input type="textarea" v-model="localPoint.Remark"
                    :placeholder="$t('pointsManagement.enterDescription')" :rows="3" maxlength="200"
                    show-word-limit></el-input>
            </el-form-item>
            <el-form-item :label="$t('pointsManagement.mapPreview')">
                <div class="dialog-map-container">
                    <KingLongMap ref="dialogMap" :mapId="'dialogPointsMap'" v-if="visible"
                        @DrawPointCompleted="handleDialogPointCompleted" @LoadCompleted="handleDialogMapLoad" />
                </div>
                <p class="map-tip">{{ $t('pointsManagement.mapTip') }}</p>
            </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer">
            <el-button size="small" @click="closeDialog">{{ $t('pointsManagement.cancelBtn') }}</el-button>
            <el-button type="primary" size="small" @click="savePoint" :loading="saving">{{ $t('pointsManagement.saveBtn') }}</el-button>
        </div>
    </el-dialog>
</template>

<script>
import KingLongMap from '@/components/KingLongMap.vue';
import { savePointsInfo } from '@/http/vehicle';

export default {
    name: 'PointEditDialog',
    components: {
        KingLongMap
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        point: {
            type: Object,
            default: () => ({
                WayPointID: 0,
                WayPointName: '',
                Longitude: '',
                Latitude: '',
                Remark: '',
                RangeRadius: 200,
                MaxLeaveTime: 15,
                IsEnabled: 1,
                BoundVehicles: []
            })
        },
        pointsList: {
            type: Array,
            default: () => []
        }
    },
    data() {
        const validateName = (rule, value, callback) => {
            if (!value || !value.trim()) {
                callback(new Error(this.$t('pointsManagement.nameRequired')));
                return;
            }
            const duplicate = this.pointsList.find(
                p => p.WayPointName === value.trim() && p.WayPointID !== this.localPoint.WayPointID
            );
            if (duplicate) {
                callback(new Error(this.$t('pointsManagement.nameDuplicate')));
                return;
            }
            callback();
        };

        return {
            saving: false,
            IsEnabled: true,
            localPoint: { ...this.point },
            rules: {
                WayPointName: [
                    { required: true, validator: validateName, trigger: 'blur' }
                ],
                Longitude: [
                    { required: true, message: this.$t('pointsManagement.coordinateRequired'), trigger: 'change' }
                ],
                Latitude: [
                    { required: true, message: this.$t('pointsManagement.coordinateRequired'), trigger: 'change' }
                ],
                RangeRadius: [
                    { required: true, message: this.$t('pointsManagement.rangeRequired'), trigger: 'blur' }
                ]
            }
        };
    },
    watch: {
        visible(val) {
            if (val) {
                this.localPoint = { ...this.point };
                this.IsEnabled = this.localPoint.IsEnabled == 1;
            }
        },
        'localPoint.RangeRadius'() {
            if (this.visible && this.localPoint.Longitude && this.localPoint.Latitude) {
                this.updateDialogRangeCircle();
            }
        }
    },
    methods: {
        closeDialog() {
            this.$emit('update:visible', false);
        },
        onDialogClosed() {
            if (this.$refs.dialogMap) {
                this.$refs.dialogMap.cancelDraw();
            }
        },
        savePoint() {
            this.$refs.pointForm.validate((valid) => {
                if (!valid) return;

                this.saving = true;
                savePointsInfo({
                    Longitude: this.localPoint.Longitude,
                    Latitude: this.localPoint.Latitude,
                    PointID: this.localPoint.WayPointID,
                    PointName: this.localPoint.WayPointName,
                    PointType: 4,
                    Remark: this.localPoint.Remark,
                    RangeRadius: this.localPoint.RangeRadius,
                    MaxLeaveTime: this.localPoint.MaxLeaveTime,
                    IsEnabled: this.IsEnabled ? 1 : 0,
                    Flag: this.localPoint.WayPointID ? 2 : 1
                }).then(res => {
                    this.saving = false;
                    if (res.code == 200) {
                        this.$message.success(this.$t('pointsManagement.saveSuccess'));
                        this.$emit('save');
                    } else {
                        this.$message.error(this.$t('pointsManagement.saveError'));
                    }
                }).catch(() => {
                    this.$message.error(this.$t('pointsManagement.saveError'));
                    this.saving = false;
                });
            });
        },
        handleDialogPointCompleted(lng, lat) {
            this.localPoint.Longitude = lng.toString();
            this.localPoint.Latitude = lat.toString();
            this.updateDialogRangeCircle();
        },
        handleDialogMapLoad() {
            if (this.localPoint.Longitude && this.localPoint.Latitude) {
                const lng = parseFloat(this.localPoint.Longitude);
                const lat = parseFloat(this.localPoint.Latitude);
                this.$refs.dialogMap.setCenter(lng, lat);

                this.$refs.dialogMap.addMarker(
                    this.localPoint.WayPointID || 'current',
                    this.localPoint.WayPointName || this.$t('pointsManagement.currentPoint'),
                    lng, lat, null, null, null, null,
                    {
                        draggable: true,
                        onDragEnd: (dragLng, dragLat) => {
                            this.localPoint.Longitude = dragLng.toString();
                            this.localPoint.Latitude = dragLat.toString();
                            this.updateDialogRangeCircle();
                        }
                    }
                );

                if (this.localPoint.RangeRadius) {
                    this.$refs.dialogMap.addCircle(
                        'dialog_circle',
                        { lng, lat },
                        this.localPoint.RangeRadius,
                        2,
                        '#409EFF',
                        'rgba(64,158,255,0.15)'
                    );
                }
            }
            if (!this.localPoint.WayPointID) {
                this.$refs.dialogMap.drawPoint();
            }
        },
        updateDialogRangeCircle() {
            if (!this.$refs.dialogMap || !this.$refs.dialogMap.map) return;
            const lng = parseFloat(this.localPoint.Longitude);
            const lat = parseFloat(this.localPoint.Latitude);
            if (isNaN(lng) || isNaN(lat)) return;

            try { this.$refs.dialogMap.removeCircleById('dialog_circle'); } catch (e) { /* ignore */ }

            if (this.localPoint.RangeRadius) {
                this.$refs.dialogMap.addCircle(
                    'dialog_circle',
                    { lng, lat },
                    this.localPoint.RangeRadius,
                    2,
                    '#409EFF',
                    'rgba(64,158,255,0.15)',
                    false
                );
            }
        },
        startRedraw() {
            if (this.$refs.dialogMap) {
                this.$refs.dialogMap.drawPoint();
            }
        }
    }
};
</script>

<style scoped>
.dialog-map-container {
    height: 280px;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
}

.map-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 6px;
    margin-bottom: 0;
}

.unit-hint {
    display: block;
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
    line-height: 1;
}
</style>